import { BasePlugin, Meta, Body } from '@uppy/core'
import { storage } from '../firebase'
import { ref, uploadBytesResumable } from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'
import { filterNonFailedFiles, filterFilesToEmitUploadStarted } from '@uppy/utils/lib/fileFilters'
import { refreshAuthTokens } from './fetch'

export default class FirebaseStorageUploadDocuments<M extends Meta, B extends Body> extends BasePlugin<any, M, B> {
  constructor(uppy, opts) {
    super(uppy, opts)
    this.id = opts?.id || 'FirebaseStorageUploadDocuments'
    this.type = 'uploader'
    this.handleUpload = this.handleUpload.bind(this)
  }

  async handleUpload(fileIDs: string[]) {
    const files = this.uppy.getFilesByIds(fileIDs)
    const filesFiltered = filterNonFailedFiles(files)
    const filesToEmit = filterFilesToEmitUploadStarted(filesFiltered)
    this.uppy.emit('upload-start', filesToEmit)

    // Refresh tokens immediately before starting upload to ensure we have a fresh token
    await refreshAuthTokens()

    // Start token refresh interval for long uploads
    const tokenRefreshInterval = setInterval(refreshAuthTokens, 40 * 60 * 1000) // Refresh every 40 minutes (changed from 55 to match other uploader and be more conservative)

    const promises = files
      .filter((file) => !file.isRemote)
      .map((file) => {
        return new Promise((resolve, reject) => {
          const storedFileName = uuidv4() + '.' + file.extension
          const storageRef = ref(storage, `products/documents/${storedFileName}`)
          const uploadTask = uploadBytesResumable(storageRef, file.data)

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              // Observe state change events such as progress
              // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              // Update Uppy's file progress for the current file
              this.uppy.emit('upload-progress', file, {
                uploadStarted: null,
                bytesUploaded: snapshot.bytesTransferred,
                bytesTotal: snapshot.totalBytes,
              })
              // console.log('Upload is ' + progress + '% done')
            },
            (error) => {
              // Handle unsuccessful uploads
              this.uppy.emit('upload-error', file, error)
              console.error(error)
              reject(error)
            },
            async () => {
              // Handle successful uploads on complete
              this.uppy.emit('upload-success', file, {
                status: null,
                uploadURL: storedFileName,
              })
              // Update Uppy's file state with the download URL
              this.uppy.setFileState(file.id, {
                uploadURL: storedFileName,
              })
              resolve(file)
            }
          )
        })
      })

    try {
      const result = await Promise.all(promises)
      // Clear token refresh interval after all uploads complete
      clearInterval(tokenRefreshInterval)
      return result
    } catch (error) {
      // Clear token refresh interval if there's an error
      clearInterval(tokenRefreshInterval)
      throw error
    }
  }

  install() {
    this.uppy.addUploader(this.handleUpload)
  }

  uninstall() {
    this.uppy.removeUploader(this.handleUpload)
  }
}
